// Display config for the Estado check-in: band colors, labels and fallback
// recommendations. The score itself is calculated by the server when the
// check-in is saved (athletain-backend src/lib/tracking/status-score.ts).
// Meals don't affect it.

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const STATUS_CONFIGS = [
  {
    minScore: 80,
    label: "Optimo",
    summary:
      "Tu cuerpo esta en condiciones ideales para rendir al maximo. Sigue asi.",
    accentColor: "#1fc18a",
    trackColor: "#d6efe8",
    badgeBg: "#d8ffe7",
    badgeText: "#1a8228",
    recommendations: [
      {
        title: "Recuperacion muscular completa",
        body: "Tus musculos estan listos. Es un buen momento para sesiones de alta intensidad o trabajo de fuerza maxima.",
        badge: "Prioriza tu entrenamiento.",
      },
      {
        title: "Sueno reparador detectado",
        body: "Dormir bien acelera la sintesis proteica y consolida la memoria motriz. Aprovecha este estado para introducir nuevos patrones de movimiento.",
        badge: "Introducir nuevos patrones de movimiento.",
      },
    ],
  },
  {
    minScore: 61,
    label: "Precaucion Positiva",
    summary: "Estas bien, pero hay senales que vale la pena atender.",
    accentColor: "#f2c94c",
    trackColor: "#f7efd0",
    badgeBg: "#fff4cc",
    badgeText: "#a06a00",
    recommendations: [
      {
        title: "Estres moderado: Monitorea tu carga mental",
        body: "Tu nivel de estres es manejable pero presente. Considera tecnicas de respiracion o mindfulness antes del entrenamiento para optimizar el rendimiento.",
        badge: "Regula tu carga mental.",
      },
      {
        title: "Sueno aceptable: Optimiza tu descanso para rendir mas",
        body: "Dormiste lo suficiente, pero no de forma optima. Mejorar la calidad del sueno puede traducirse directamente en mayor recuperacion y concentracion.",
        badge: "Mejora tu descanso.",
      },
      {
        title: "Leve fatiga muscular: Ajusta la intensidad hoy",
        body: "Hay fatiga muscular leve presente. Puedes entrenar, pero considera reducir la carga un 15-20% para no comprometer la recuperacion.",
        badge: "Reduce un poco la carga.",
      },
    ],
  },
  {
    minScore: 41,
    label: "Precaucion Negativa",
    summary:
      "Tu cuerpo esta enviando senales de alerta. Ajusta tu entrenamiento.",
    accentColor: "#ff8a3d",
    trackColor: "#ffe3d1",
    badgeBg: "#ffe7d6",
    badgeText: "#c65d1a",
    recommendations: [
      {
        title: "DOMS moderados: Reduce la carga de entrenamiento",
        body: "El dolor muscular moderado indica que las fibras aun estan en proceso de reparacion. Continuar con alta intensidad puede frenar la recuperacion o derivar en lesion.",
        badge: "Reduce la carga hoy.",
      },
      {
        title: "Dolor articular leve: No ignores esta senal",
        body: "El dolor articular, aunque leve, no debe ignorarse. A diferencia del dolor muscular, puede indicar inflamacion o desgaste que empeora con el ejercicio.",
        badge: "Evalua molestias articulares.",
      },
      {
        title: "Fatiga acumulada: El descanso es parte del entrenamiento",
        body: "La fatiga acumulada sin recuperacion adecuada reduce el rendimiento y aumenta el riesgo de lesion. Una sesion de recuperacion activa hoy puede ser mas beneficiosa que entrenar fuerte.",
        badge: "Prioriza recuperacion activa.",
      },
    ],
  },
  {
    minScore: 0,
    label: "Alto Riesgo",
    summary:
      "Tu cuerpo necesita recuperacion urgente. Entrenar hoy puede causar lesion.",
    accentColor: "#ef4444",
    trackColor: "#fde2e2",
    badgeBg: "#fee2e2",
    badgeText: "#b42318",
    recommendations: [
      {
        title: "DOMS severos + fatiga acumulada: Riesgo de lesion alto",
        body: "Las fibras musculares no han reparado. Continuar entrenando puede derivar en una lesion estructural como desgarro o rotura de fibras. Se recomienda descanso total o recuperacion activa leve.",
        badge: "Deten la carga intensa.",
      },
      {
        title: "Frecuencia cardiaca en reposo elevada",
        body: "Senal directa de sobre entrenamiento o enfermedad. El cuerpo esta bajo estres fisiologico alto. Continuar puede agravar el estado y prolongar el tiempo de recuperacion.",
        badge: "Monitorea tu recuperacion.",
      },
      {
        title: "Estres critico: Sistema nervioso sobrecargado",
        body: "Los niveles de cortisol estan inhibiendo activamente la recuperacion y aumentando el riesgo de burnout deportivo. Se recomienda descanso, tecnicas de relajacion y evaluacion medica si persiste.",
        badge: "Activa protocolo de recuperacion.",
      },
    ],
  },
];

export const getStatusConfig = (score) =>
  STATUS_CONFIGS.find((config) => score >= config.minScore) ||
  STATUS_CONFIGS[STATUS_CONFIGS.length - 1];

const getStatusConfigByLabel = (label) =>
  STATUS_CONFIGS.find((config) => config.label === label);

const NO_STATUS_SUMMARY = {
  hasData: false,
  percentage: 0,
  label: "Sin datos",
  summary:
    "Actualiza tu estado para calcular tu score y recibir recomendaciones personalizadas.",
  accentColor: "#c7d2e5",
  trackColor: "#edf1f7",
  badgeBg: "#eef2f7",
  badgeText: "#475467",
  recommendations: [],
};

export const buildStatusSummary = (dailyStatus) => {
  // An entry without a server score (old demo data) has nothing to show.
  const score = Number(dailyStatus?.score);
  if (
    dailyStatus?.score === undefined ||
    dailyStatus?.score === null ||
    !Number.isFinite(score)
  ) {
    return NO_STATUS_SUMMARY;
  }

  const percentage = clamp(score, 0, 100);
  const statusConfig =
    getStatusConfigByLabel(dailyStatus?.status) || getStatusConfig(percentage);
  const recommendations = Array.isArray(dailyStatus?.recommendations)
    ? dailyStatus.recommendations.filter(
        (recommendation) =>
          recommendation?.title && recommendation?.body && recommendation?.badge,
      )
    : [];

  return {
    hasData: true,
    percentage,
    ...statusConfig,
    recommendations:
      recommendations.length > 0
        ? recommendations
        : statusConfig.recommendations,
  };
};

// The check-in answers as PUT /athlete-tracking/by-date/:date/daily-status
// expects them. The server calculates score, status and recommendations.
export const buildDailyStatusAnswers = (formData) => ({
  sleep: {
    hours: Number(formData.sleepHours),
    quality: Number(formData.sleepQuality),
  },
  training: {
    rpe: Number(formData.rpe),
    sessionDuration: Number(formData.sessionDuration),
    fatigue: Number(formData.fatigue),
    doms: Number(formData.doms),
  },
  mindset: {
    mood: Number(formData.mood),
    motivation: Number(formData.motivation),
  },
});
